import type { BusinessRisk } from '@/services/quotes/types';
import { formatZipcode } from '@/shared/utils/format-zipcode.util';
import type {
  BusinessLocation,
  BusinessLocationBenefit,
  BusinessLocationCoverage,
  BusinessLocationPolicyLimitDeductible,
} from '../components/quote-steps/business-location';

export function isAssistanceCoverageCode(code?: string): boolean {
  const upper = (code || '').trim().toUpperCase();
  return upper === 'ASSIST' || upper.endsWith('ASS');
}

export function isCapitaCoverageCode(code?: string): boolean {
  const upper = (code || '').trim().toUpperCase();
  return upper === 'CAPITA' || upper.endsWith('CAP');
}

/**
 * Converte as franquias estruturadas da cobertura (resposta do BFF) para o
 * formato do payload de attach (`policyLimitDeductible`). Itens sem os
 * campos obrigatórios do contrato (`id`, `code`, `minValue`, `name`) são
 * descartados — nesse caso o campo é omitido e o BFF preserva a lista já
 * persistida no InsureMO.
 */
function mapPolicyLimitDeductibleFromResponse(
  items: NonNullable<BusinessRisk['coverages']>[number]['policyLimitDeductible']
): BusinessLocationPolicyLimitDeductible[] | undefined {
  if (!items?.length) return undefined;
  const mapped = items
    .filter(
      item =>
        item.productElementId != null &&
        item.productElementCode &&
        item.name &&
        item.limitDeductibleValue != null
    )
    .map(item => ({
      id: String(item.productElementId),
      code: String(item.productElementCode),
      minValue: item.limitDeductibleValue as number,
      name: item.name as string,
      ...(item.description ? { description: item.description } : {}),
      ...(item.activityGroup != null
        ? { activityGroup: item.activityGroup }
        : {}),
    }));
  return mapped.length > 0 ? mapped : undefined;
}

export function mapCoveragesToBusinessLocationPayload(
  coverages: BusinessRisk['coverages']
): BusinessLocationCoverage[] {
  if (!coverages?.length) return [];
  return coverages
    .filter(
      c =>
        !c.planCode &&
        !isAssistanceCoverageCode(c.productElementCode) &&
        !isCapitaCoverageCode(c.productElementCode)
    )
    .map(c => {
      const policyLimitDeductible = mapPolicyLimitDeductibleFromResponse(
        c.policyLimitDeductible
      );
      return {
        code: c.productElementCode ?? '',
        value: c.sumInsured ?? 0,
        ...(c.productElementId
          ? { productElementId: String(c.productElementId) }
          : {}),
        ...(c.codeDeductible != null
          ? { codeDeductible: String(c.codeDeductible) }
          : {}),
        ...(policyLimitDeductible ? { policyLimitDeductible } : {}),
      };
    })
    .filter(c => c.code && c.value > 0);
}

function findAssistanceCoverage(coverages: BusinessRisk['coverages']) {
  if (!coverages?.length) return undefined;
  const assistCoverages = coverages.filter(c =>
    isAssistanceCoverageCode(c.productElementCode)
  );
  if (assistCoverages.length === 0) return undefined;
  // Cotações antigas podem ter uma segunda linha de assistência fantasma
  // persistida sem sumInsured (bug 178); a linha real enviada pelo
  // portal sempre leva sumInsured (0). Preferimos a real para o card
  // resolver o pacote correto mesmo antes da auto-cura no backend.
  return (
    assistCoverages.find(c => c.sumInsured !== undefined && c.sumInsured !== null) ??
    assistCoverages[0]
  );
}

export function resolveAssistanceFromCoverages(
  coverages: BusinessRisk['coverages']
): { packageName: string; packageCode: string } {
  const assistCoverage = findAssistanceCoverage(coverages);
  if (!assistCoverage) return { packageName: '', packageCode: '' };
  const packageCode =
    assistCoverage.planCode || assistCoverage.productElementCode || '';
  if (!packageCode) return { packageName: '', packageCode: '' };
  return {
    packageName: assistCoverage.productElementDesc || packageCode,
    packageCode,
  };
}

export function resolveBenefitsFromCoverages(
  coverages: BusinessRisk['coverages']
): BusinessLocationBenefit[] {
  const assistCoverage = findAssistanceCoverage(coverages);
  const nestedBenefits = assistCoverage?.benefits ?? [];
  return nestedBenefits
    .map(benefit => ({
      code: String(benefit.productElementCode ?? '').trim(),
      value: benefit.sumInsured ?? 0,
      ...(benefit.productElementId != null
        ? { productElementId: String(benefit.productElementId) }
        : {}),
    }))
    .filter(b => b.code);
}

function normalizeMultiAnswer(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return [];
}

export function resolveBusinessQuestionnaireAnswers(
  risk: BusinessRisk
): (string | string[])[] {
  return [
    normalizeMultiAnswer(risk.fireProtectionSystems),
    risk.isUpperFloor ?? '',
    risk.isInsideMall ?? '',
    normalizeMultiAnswer(risk.securityAlarmSystems),
  ];
}

export type MapBusinessRisksOptions = {
  /** Usa `pk` do risco para `id` estável entre refetches (ex.: step de cálculo). */
  stableIds?: boolean;
};

export function mapBusinessRisksToLocations(
  risks: BusinessRisk[],
  options?: MapBusinessRisksOptions
): BusinessLocation[] {
  const stableIds = options?.stableIds === true;

  return risks.map((risk, index) => {
    const firstSchedule = risk.scheduleExt?.[0];
    const riskValueNumber = firstSchedule?.value ?? 0;
    const riskValue =
      riskValueNumber > 0
        ? riskValueNumber.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          })
        : '';

    const businessInterruptionNumber = risk.businessInterruptionAmount ?? 0;
    const businessInterruptionValue =
      businessInterruptionNumber > 0
        ? businessInterruptionNumber.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          })
        : '';

    const { packageName, packageCode } = resolveAssistanceFromCoverages(
      risk.coverages
    );

    return {
      id: stableIds ? String(risk.pk ?? index) : crypto.randomUUID(),
      riskPk: risk.pk,
      cep: formatZipcode(risk.zipCode || ''),
      street: risk.address || '',
      number: risk.addressNumber || '',
      complement: risk.addressComplement || '',
      neighborhood: risk.neighborhood || '',
      city: risk.city || '',
      state: risk.state || '',
      activityType:
        risk.activityTypePerformed || risk.tipoDeAtividadeRealizada || '',
      activityTypeLabel:
        risk.activityTypePerformed || risk.tipoDeAtividadeRealizada || '',
      constructionType: risk.constructionType || '',
      constructionTypeLabel: risk.constructionType || '',
      isLandmarked: risk.listedPropertyFlag === 'Y',
      insuredObjectType: firstSchedule?.insuredObject ?? '',
      insuredObjectTypeLabel: firstSchedule?.insuredObject ?? '',
      riskValue,
      businessInterruptionValue,
      ...(risk.underwritingDiscountRequestInd
        ? {
            underwritingDiscountRequestInd:
              risk.underwritingDiscountRequestInd === 'Y'
                ? ('Y' as const)
                : ('N' as const),
          }
        : {}),
      assistancePackage: packageName,
      assistancePackageCode: packageCode,
      questionnaireAnswers: resolveBusinessQuestionnaireAnswers(risk),
      coveragesPayload: mapCoveragesToBusinessLocationPayload(risk.coverages),
      benefitsPayload: resolveBenefitsFromCoverages(risk.coverages),
      propertyType: risk.propertyType || '',
      propertyClassification: risk.propertyClassification || '',
    };
  });
}
